import { IncomingMessage } from 'http';

import { getPlaceFromLocationSlug } from '@/src/homepage/api/geo/getPlaceFromLocationSlug';
import type { Location } from '@/src/homepage/types';

import { EMPTY_LOCATION, type GeoPlace, placeToLocation } from './getUserLocationFromRequest';

export const EB_LOCATION_SLUG_HEADER = 'eb-location-slug';

/**
 * Resolves the location for a shared-cached document.
 *
 * The document is cached at the CDN under a key that contains only
 * `eb-host`, `eb-lang`, `eb-location-slug`, `x-eb-origin-bucket` and
 * `x-eb-homepage-debug`, so the edge location slug is the only per-viewer
 * input the SSR may read: the viewer coordinates, the client IP and the
 * `location` cookie all sit outside that key, and using any of them would bake
 * one viewer's place into an entry served to everyone in the same slug bucket.
 * A missing or unresolved slug yields `EMPTY_LOCATION`, which the client then
 * refines through the uncached BFF.
 */
export const getDocumentLocationFromRequest = async (
    req: IncomingMessage | undefined,
): Promise<Location> => {
    if (!req) {
        return EMPTY_LOCATION;
    }

    const locationSlug = req.headers[EB_LOCATION_SLUG_HEADER] as string | undefined;
    if (!locationSlug) {
        return EMPTY_LOCATION;
    }

    const place = (await getPlaceFromLocationSlug(locationSlug)) as GeoPlace | null;
    if (!place?.id) {
        return EMPTY_LOCATION;
    }

    return placeToLocation(place);
};
